import { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import type { TocHeading } from '../../services/docs';
import { getActiveTocHeadingId, READING_LINE_OFFSET } from './docsTocActiveHeading';

// Height of a single TOC row in px. Must match `.docs-toc-item` /
// `.docs-toc-indicator` height in docs.css so the active indicator lines up.
const TOC_ITEM_HEIGHT = 28;

function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  while (el) {
    const style = getComputedStyle(el);
    if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

interface DocsTocProps {
  headings: TocHeading[];
  className?: string;
}

function DocsToc({ headings, className }: DocsTocProps) {
  const [activeId, setActiveId] = useState<string>('');
  const [leftOffset, setLeftOffset] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const isPinnedToBottomRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    setActiveId(headings.length > 0 ? headings[0].id : '');
    isPinnedToBottomRef.current = false;
  }, [headings]);

  const updateActiveHeading = useCallback(() => {
    if (headings.length === 0) return;

    const scrollParent = scrollParentRef.current;
    const headingIds: string[] = [];
    const headingTops: number[] = [];

    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (!el) continue;
      headingIds.push(heading.id);
      headingTops.push(el.getBoundingClientRect().top);
    }

    if (headingIds.length === 0) return;

    const scrollTop = scrollParent?.scrollTop ?? window.scrollY;
    const scrollHeight =
      scrollParent?.scrollHeight ?? document.documentElement.scrollHeight;
    const clientHeight = scrollParent?.clientHeight ?? window.innerHeight;
    const readingLineY = scrollParent
      ? scrollParent.getBoundingClientRect().top + READING_LINE_OFFSET
      : READING_LINE_OFFSET;

    const { activeId: newActiveId, isPinnedToBottom } = getActiveTocHeadingId({
      headingIds,
      headingTops,
      scrollTop,
      scrollHeight,
      clientHeight,
      readingLineY,
      isPinnedToBottom: isPinnedToBottomRef.current,
    });

    isPinnedToBottomRef.current = isPinnedToBottom;
    setActiveId(newActiveId);
  }, [headings]);

  const scheduleUpdateActiveHeading = useCallback(() => {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      updateActiveHeading();
    });
  }, [updateActiveHeading]);

  // Measure the wrapper's horizontal position so the fixed TOC aligns with it
  useEffect(() => {
    function measure() {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setLeftOffset(rect.left);
      }
    }

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (headings.length === 0) return;

    const firstEl = document.getElementById(headings[0].id);
    scrollParentRef.current = getScrollParent(firstEl);
    const scrollTarget = scrollParentRef.current ?? window;

    updateActiveHeading();

    scrollTarget.addEventListener('scroll', scheduleUpdateActiveHeading, {
      passive: true,
    });
    window.addEventListener('resize', scheduleUpdateActiveHeading);

    return () => {
      scrollTarget.removeEventListener('scroll', scheduleUpdateActiveHeading);
      window.removeEventListener('resize', scheduleUpdateActiveHeading);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [headings, updateActiveHeading, scheduleUpdateActiveHeading]);

  if (headings.length === 0) return null;

  const activeIndex = headings.findIndex((h) => h.id === activeId);

  return (
    <div ref={wrapperRef} className={clsx('docs-page-toc', className)}>
      <nav
        className="docs-toc"
        aria-label="Table of contents"
        style={leftOffset !== null ? { left: leftOffset } : undefined}
      >
        <div className="docs-toc-list-wrapper">
          <div
            className="docs-toc-indicator"
            style={{
              transform:
                activeIndex >= 0
                  ? `translateY(${activeIndex * TOC_ITEM_HEIGHT}px)`
                  : 'translateY(-100%)',
              opacity: activeIndex >= 0 ? 1 : 0,
            }}
          />
          <ul className="docs-toc-list">
            {headings.map((heading) => (
              <li key={heading.id}>
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(heading.id);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setActiveId(heading.id);
                    }
                  }}
                  className={clsx(
                    'docs-toc-item',
                    heading.level === 3 && 'docs-toc-item-nested',
                    activeId === heading.id && 'docs-toc-item-active',
                  )}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  );
}

export default DocsToc;
